import { useContext, useMemo } from 'react';
import type { FeapiError } from '@edreamsodigeo/frontend-api-client';

import type {
  CheckoutFormAcceptTermConditionContextType,
  CheckoutFormRefStateContextType,
  CheckoutStateContextType,
} from '@/clientState/providers/checkout/types';
import type {
  CheckoutMutationHsaMutation,
  CheckoutMutationMutation,
  CollectionMethod,
  CollectionMethodInfo,
} from '@/generated/graphql';
import { CheckoutStatus, PaymentMethod } from '@/generated/graphql';
import {
  AlternativePaymentMethodSelectedContext,
  CheckoutCollectionOptionsStateContext,
  CheckoutFormAcceptTermConditionContext,
  CheckoutFormRefStateContext,
  CheckoutPrimeEMLStatusContext,
  CheckoutResponseStateContext,
  CheckoutStateContext,
  PaymentInstrumentTokenContext,
  SaveCreditCardContext,
  ShoppingBasketIdContext,
  ShowSumbmitValidationContext,
  SpecialRequestStateContext,
} from './CheckoutStateContext';
import type {
  AlternativePaymentMethodSelected,
  CheckoutPrimeEMLStatusContextType,
  CheckoutResponseStateContextType,
  CreditCardPCIDataType,
  PaymentInstrumentTokenContextType,
  ShoppingBasketIdContextType,
  SpecialRequestStateContextType,
} from '@/clientState/providers/checkout/types/CheckoutStateContext';
import { StoredCreditCardType } from '@/clientState/providers/checkout/types/CheckoutStateContext';
import {
  getPCIBinFromEntry,
  getPCIExpirationMonthFromEntry,
  getPCIExpirationYearFromEntry,
} from './creditCardPCIConstants';
import type { UseMutationType } from '@/mutations/types';
import type { ClientCheckoutStatus } from '@/clientState/providers/checkout/types/CheckoutActionsContext';
import { APM_CONF } from './APM.conf';
import { usePaymentFormIframeConfig } from '@/clientState/providers/checkout/PaymentFormIframeConfigAB';

export const useCheckoutCallState = (): boolean => {
  const { loading } = useContext(CheckoutResponseStateContext);
  return useMemo(() => loading, [loading]);
};

export const useCheckoutResponsePaymentInteractionId = (): string => {
  const { data } = useContext(CheckoutResponseStateContext);

  return useMemo(
    () => data?.checkout?.userPaymentInteractionId?.id || '',
    [data?.checkout?.userPaymentInteractionId?.id]
  );
};

export const useCheckoutResponse = (): CheckoutResponseStateContextType => {
  return useContext(CheckoutResponseStateContext);
};

export const useSuccessCheckoutResponseData =
  (): CheckoutResponseStateContextType['data'] => {
    const { data } = useContext(CheckoutResponseStateContext);
    return data?.checkout?.statusInfo?.status === CheckoutStatus.Success
      ? data
      : null;
  };

export const useShowSubmitValidation = (): boolean => {
  return useContext(ShowSumbmitValidationContext);
};

export const useCheckoutPrimeEMLStatus =
  (): CheckoutPrimeEMLStatusContextType => {
    return useContext(CheckoutPrimeEMLStatusContext);
  };

export const useResetForm = (): CheckoutStateContextType['reset'] => {
  const { reset } = useContext(CheckoutStateContext);
  return reset;
};

export const useIsValidCheckoutForm =
  (): CheckoutStateContextType['isValidCheckoutForm'] => {
    const { isValidCheckoutForm } = useContext(CheckoutStateContext);
    return isValidCheckoutForm;
  };

export const useResetIndividualFieldsState =
  (): CheckoutStateContextType['resetIndividualFields'] => {
    const { resetIndividualFields } = useContext(CheckoutStateContext);
    return resetIndividualFields;
  };

export const useSelectedPaymentMethod =
  (): CheckoutStateContextType['selectedPaymentMethod'] => {
    const { selectedPaymentMethod } = useContext(CheckoutStateContext);
    return selectedPaymentMethod;
  };

export const useSelectedPaymentMethodType =
  (): CheckoutStateContextType['selectedPaymentMethod']['collectionMethodType'] => {
    const {
      selectedPaymentMethod: { collectionMethodType },
    } = useContext(CheckoutStateContext);
    return collectionMethodType;
  };

export const useSelectedPaymentMethodInfo = (): CollectionMethodInfo => {
  const {
    selectedPaymentMethod: {
      collectionMethodType: selectedCollectionMethodType,
      creditCardCollection,
      storedCard,
    },
  } = useContext(CheckoutStateContext);
  const isStoredCard =
    selectedCollectionMethodType === StoredCreditCardType.STORED_CARD;
  const isCardStoredInRetailSession =
    selectedCollectionMethodType === StoredCreditCardType.RETAIL_SESSION;
  const collectionMethodCode = useMemo(
    () =>
      isStoredCard
        ? storedCard?.cardTypeCode || null
        : creditCardCollection?.cardTypeCode || null,
    [isStoredCard, storedCard?.cardTypeCode, creditCardCollection?.cardTypeCode]
  );
  const collectionMethodType = useMemo(
    () =>
      isStoredCard || isCardStoredInRetailSession
        ? PaymentMethod.Creditcard
        : selectedCollectionMethodType,
    [selectedCollectionMethodType, isStoredCard, isCardStoredInRetailSession]
  );
  return useMemo(
    () => ({
      collectionMethodCode,
      collectionMethodType,
    }),
    [collectionMethodCode, collectionMethodType]
  );
};

export const useCreditCardPCIData = (): CreditCardPCIDataType => {
  const {
    selectedPaymentMethod: { creditCardCollection },
  } = useContext(CheckoutStateContext);

  const { enabled: isIframeEnabled } = usePaymentFormIframeConfig();

  return useMemo(() => {
    const firstNumberDigits = isIframeEnabled
      ? creditCardCollection?.first8Digits || ''
      : getPCIBinFromEntry(creditCardCollection?.cardNumber);

    return {
      firstNumberDigits,
      expirationMonth: getPCIExpirationMonthFromEntry(
        creditCardCollection?.cardExpirationMonth
      ),
      expirationYear: getPCIExpirationYearFromEntry(
        creditCardCollection?.cardExpirationYear
      ),
    };
  }, [
    creditCardCollection?.cardExpirationYear,
    creditCardCollection?.cardExpirationMonth,
    creditCardCollection?.cardNumber,
    creditCardCollection?.first8Digits,
    isIframeEnabled,
  ]);
};

export const useCheckoutError = (): UseMutationType['errors'] => {
  const { errors } = useContext(CheckoutResponseStateContext);
  return errors;
};

export const useAvailableCreditCards = (): {
  collectionOptions: Array<CollectionMethod>;
  loading?: boolean;
  errors?: Array<FeapiError>;
} => {
  const collectionOptions = useContext(CheckoutCollectionOptionsStateContext);
  return useMemo(
    () => ({
      collectionOptions: collectionOptions?.collectionOptions.filter(
        ({ type }) => type === PaymentMethod.Creditcard
      ),
      loading: collectionOptions?.loading,
      errors: collectionOptions?.errors,
    }),
    [collectionOptions]
  );
};

export const useAlternativePaymentsMethods = (): Array<CollectionMethod> => {
  const { collectionOptions } = useContext(
    CheckoutCollectionOptionsStateContext
  );
  return useMemo<CollectionMethod[]>(
    () =>
      collectionOptions.filter(
        ({ type }) =>
          type !== PaymentMethod.Creditcard &&
          (!APM_CONF[type as PaymentMethod]?.shouldShow ||
            APM_CONF[type as PaymentMethod]?.shouldShow?.())
      ),
    [collectionOptions]
  );
};

export const useCheckoutStatus = (): ClientCheckoutStatus => {
  const { data } = useContext(CheckoutResponseStateContext);
  return data?.checkout?.statusInfo?.status || 'INITIAL';
};

export const useCheckoutRepricing = ():
  | CheckoutMutationMutation['checkout']['repricing']
  | CheckoutMutationHsaMutation['checkout']['repricing'] => {
  const { data } = useContext(CheckoutResponseStateContext);
  return data?.checkout?.repricing ? data.checkout.repricing : null;
};

export const useSpecialRequirements =
  (): SpecialRequestStateContextType['value'] => {
    const specialRequirements = useContext(SpecialRequestStateContext);
    return specialRequirements?.value;
  };

export const useSaveCreditCard = (): boolean => {
  return useContext<boolean>(SaveCreditCardContext);
};

export const useHasAcceptedTerms =
  (): CheckoutFormAcceptTermConditionContextType => {
    return useContext(CheckoutFormAcceptTermConditionContext);
  };

export const useSetFormRef = (): CheckoutFormRefStateContextType => {
  return useContext(CheckoutFormRefStateContext);
};

export const usePaymentInstrumentToken =
  (): PaymentInstrumentTokenContextType => {
    return useContext(PaymentInstrumentTokenContext);
  };

export const useAlternativePaymentMethodSelected =
  (): AlternativePaymentMethodSelected => {
    return useContext(AlternativePaymentMethodSelectedContext);
  };

export const useShoppingIdFromContextProvider =
  (): ShoppingBasketIdContextType => {
    return useContext(ShoppingBasketIdContext);
  };
